import type { ErrorLike } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { useCallback } from 'react'
import {
  RequestedSourcesDocument,
  type RequestedSourcesQuery,
  type RequestedSourcesQueryVariables,
  type Source,
} from '~app/graph/generated/graphql'

type SourcesQueryResult = useQuery.Result<
  RequestedSourcesQuery,
  RequestedSourcesQueryVariables
>

interface PaginationQuery {
  loading: boolean
  error: ErrorLike | undefined
  refetch: SourcesQueryResult['refetch']
  total: number
  sources: Source[]
  previous?: () => void
  next?: () => void
}

export interface WithPaginatedSourcesProps {
  paginationQuery: PaginationQuery
}

export const usePaginatedSources = (count = 50): WithPaginatedSourcesProps => {
  const { data, loading, error, refetch, fetchMore } = useQuery<
    RequestedSourcesQuery,
    RequestedSourcesQueryVariables
  >(RequestedSourcesDocument, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: { count },
  })

  const previous = useCallback(() => {
    // TODO: implement
  }, [])

  const next = useCallback(() => {
    if (!data?.sourcesConnection) {
      return
    }
    fetchMore({
      variables: {
        cursor: data.sourcesConnection.pageInfo.endCursor,
      },
      updateQuery(previousResult, { fetchMoreResult }) {
        if (!fetchMoreResult) {
          return previousResult
        }
        const {
          edges = [],
          pageInfo,
          aggregate,
        } = fetchMoreResult.sourcesConnection
        return {
          __typename: previousResult.__typename,
          sourcesConnection: {
            __typename: previousResult.sourcesConnection.__typename,
            edges: [...previousResult.sourcesConnection.edges, ...edges],
            pageInfo,
            aggregate,
          },
        }
      },
    })
  }, [data, fetchMore])

  if (!data?.sourcesConnection) {
    return {
      paginationQuery: {
        error,
        loading,
        refetch,
        sources: [],
        total: 0,
      },
    }
  }

  const { edges, pageInfo, aggregate } = data.sourcesConnection
  return {
    paginationQuery: {
      error,
      loading,
      next: pageInfo.hasNextPage ? next : undefined,
      previous: pageInfo.hasPreviousPage ? previous : undefined,
      refetch,
      sources: edges.map(edge => edge!.node as Source),
      total: aggregate.count,
    },
  }
}
